import samImage from "./sam.jpeg";
import depicto from "./proj-images/depicto.webp";
import doople from "./proj-images/doople.png";
import emotions from "./proj-images/emotions.png";
import git from "./proj-images/git.png";
import nsil from "./proj-images/nsil.png";

export default function Portfolio() {
  const projects = [
    {
      title: "Syllingo",
      categories: ["Education", "Language Learning"],
      description: "Japanese practice built for my class, using only the vocabulary, kanji, and grammar we've covered.",
      story: "I built Syllingo for my Japanese class. We had a growing list of vocabulary, kanji, and grammar patterns, but finding ways to practice exactly what we were learning was difficult. I'd been using ChatGPT to generate sentences with our grammar patterns, and wanted to make that kind of practice easier for my classmates too. Syllingo gives us a place to practice with the material we've covered so far, keeping the vocabulary familiar while we work on putting it all together.",
      link: "https://syllingo.com",
      monogram: "S"
    },
    {
      title: "Depicto",
      categories: ["AI", "Games"],
      description: "Daily AI-generated image guessing game — hit 48% day-30 retention, 6× the industry average. Started as Backwords, a hackathon project.",
      link: "https://depicto.ai",
      image: depicto
    },
    {
      title: "Nsilico",
      categories: ["AI", "Research"],
      description: "AI-powered survey response generation — persona-based answers at scale.",
      link: "https://nsilico.net",
      image: nsil
    },
    {
      title: "Git",
      categories: ["Developer Tools"],
      description: "VCS built from scratch in Java — SHA-1 hashing, blob/tree/commit model, branching, and merge.",
      link: "https://github.com/freesampul/Git",
      image: git
    },
    {
      title: "Emotional Shopping",
      categories: ["AI", "E-commerce"],
      description: "Real-time emotion-driven product recommendations via facial expression detection with Face-api.js.",
      link: "https://freesampul.github.io/EmotionalShopping/public/index.html",
      image: emotions
    },
    {
      title: "Doople",
      categories: ["Creative Tools"],
      description: "Build an avatar with hand-drawn assets",
      link: "https://doople.netlify.app/",
      image: doople
    },
  ];

  return (
    <div className="bg-black text-white min-h-screen font-mono p-4 flex flex-col items-center">
      {/* Terminal Section */}
      <header className="p-6 max-w-2xl w-full mx-auto mt-6 border border-gray-700 rounded-lg shadow-lg flex flex-col items-center text-center">
        {/* User Info */}
        <div className="flex flex-col md:flex-row items-center md:items-start space-x-0 md:space-x-4 w-full">
          {/* Profile Image */}
          <img
            src={samImage}
            alt="Sam Pulaski"
            width="96"
            height="96"
            className="w-24 h-24 rounded-full border border-gray-500 object-cover"
          />
          {/* Spacer to prevent shifting */}
          <div className="hidden md:block w-6"></div>
          {/* Text Content */}
          <div className="w-full text-center md:text-left">
            <h1 className="text-2xl font-bold text-yellow-400">sampulaski@nyu.edu</h1>
            <p className="text-gray-300">--------------------</p>
            <p className="text-gray-400">
              Hi, I'm <span className="text-indigo-400 font-bold">Sam Pulaski</span> — studying <span className="text-indigo-400 font-bold">Computer Science at NYU</span> with a minor in Business Studies. I'm interested in fintech and product. Most recently I interned at <span className="text-blue-400 font-bold">Ticketmaster</span> on the Demand team, scaling SMS workflows to 200K+ messages/day and rebuilding internal tools in React and GraphQL.
            </p>
            <p className="text-gray-300 mt-3 border-l-2 border-indigo-400 pl-3 text-left">
              <span className="text-indigo-400 text-xs uppercase tracking-wider block mb-1">My thesis</span>
              I want to build tools that make people's lives easier. More time for what matters.
            </p>
            <p className="text-gray-400 mt-2">
              Currently: <span className="text-green-400">open to summer 2027 internships</span>. Feel free to reach out.
            </p>
          </div>
        </div>

        {/* Contact & Links */}
        <div className="mt-4 w-full flex flex-col items-center text-center">
          <div className="flex flex-wrap justify-center gap-4 mt-2">
            <a href="mailto:sampulaski@nyu.edu" className="text-green-400 hover:underline"> Email</a>
            <a href="/resume.pdf" className="text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer"> Resume</a>
            <a href="https://github.com/freesampul" className="text-blue-400 hover:underline"> GitHub</a>
            <a href="https://www.linkedin.com/in/sampul" className="text-pink-400 hover:underline"> LinkedIn</a>
          </div>
        </div>
      </header>

      <main className="w-full flex flex-col items-center">
      {/* Projects Section */}
      <div className="mt-6 max-w-2xl w-full mx-auto border border-gray-700 rounded-lg shadow-lg p-4">
          <h2 className="text-lg font-bold text-yellow-400 text-center">Projects</h2>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {projects.map((project, index) => (
              <div key={index} className="border-b border-gray-600 py-4 flex flex-col items-center text-center">
                {project.image && (
                  <img
                    src={project.image}
                    alt={project.title}
                    width="96"
                    height="96"
                    loading="lazy"
                    className="w-24 h-24 shrink-0 rounded-lg border border-gray-500 mb-2 object-cover"
                  />
                )}
                {project.monogram && (
                  <div aria-hidden="true" className="w-24 h-24 shrink-0 rounded-lg border border-gray-500 bg-gray-900 mb-2 flex items-center justify-center text-[5.5rem] leading-none font-bold text-indigo-300">
                    {project.monogram}
                  </div>
                )}
                <div>
                  <h3 className="text-blue-400 text-lg font-semibold">{project.title}</h3>
                  <ul aria-label={`${project.title} categories`} className="flex flex-wrap justify-center gap-1.5 mt-1 mb-2">
                    {project.categories.map((category) => (
                      <li key={category} className="rounded-full border border-gray-700 bg-gray-900 px-2 py-0.5 text-xs text-gray-300">
                        {category}
                      </li>
                    ))}
                  </ul>
                  <p className="text-gray-300">{project.description}</p>
                  {project.story && (
                    <details className="group my-2 text-center">
                      <summary className="mx-auto flex w-fit cursor-pointer list-none items-center justify-center gap-1 py-1 text-xs text-indigo-300 rounded hover:text-indigo-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-400 [&::-webkit-details-marker]:hidden">
                        Why I built it
                        <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" className="h-3 w-3 transition-transform group-open:rotate-180">
                          <path d="m4 6 4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </summary>
                      <p className="mt-2 text-left text-sm leading-relaxed text-gray-300">{project.story}</p>
                    </details>
                  )}
                  <a href={project.link} target="_blank" rel="noopener noreferrer" className="text-green-400 hover:underline">View Project</a>
                </div>
              </div>
            ))}
          </div>
      </div>

      {/* Awards and Accolades Section */}
      <div className="mt-6 max-w-2xl w-full mx-auto border border-gray-700 rounded-lg shadow-lg p-6">
        <h2 className="text-lg font-bold text-yellow-400 text-center mb-4">Awards and Accolades</h2>
        <div className="space-y-3 text-center">
          <div className="text-gray-300">
            Leadership award - 4th grade class
          </div>
          <div className="text-gray-300">
            "The most handsome boy on earth" - Mom
          </div>
          <div className="text-gray-300">
            "Most I've ever seen anyone eat" - Buffalo Wild Wings waiter, all you can eat promotion
          </div>
        </div>
      </div>
      </main>
    </div>
  );
}
